import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { contentService } from '../services/contentService';

const AUTOPLAY_MS = 7000;

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadFeaturedContent = async () => {
      try {
        setLoading(true);
        const content = await contentService.getFeaturedContent(5);
        if (content?.length > 0) {
          setFeaturedContent(content);
        } else {
          setFeaturedContent([]);
        }
      } catch (err) {
        console.error('Error loading featured content:', err);
        setFeaturedContent([]);
      } finally {
        setLoading(false);
      }
    };
    loadFeaturedContent();
  }, []);

  const current = featuredContent[currentSlide];
  const total = featuredContent.length;

  useEffect(() => {
    if (!isAutoPlaying || total <= 1) return;
    const t = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % total);
    }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [isAutoPlaying, total]);

  const heroHeight =
    'min-h-[360px] h-[50vh] sm:h-[58vh] md:h-[65vh] lg:h-[70vh] max-h-[800px]';

  if (loading) {
    return (
      <section
        className={cn(
          'relative w-full overflow-hidden bg-background',
          heroHeight
        )}
      >
        <div className="absolute inset-0 animate-pulse bg-muted/20" />
      </section>
    );
  }

  if (!current || featuredContent.length === 0) {
    return (
      <section
        className={cn(
          'relative flex w-full items-center justify-center overflow-hidden bg-background',
          heroHeight
        )}
      >
        <p className="text-sm text-muted-foreground">No featured content yet.</p>
      </section>
    );
  }

  const genres = current.genre || [];
  const primaryGenre = genres[0];
  const metaParts = [
    primaryGenre,
    current.year,
    current.duration,
    current.rating ? `★ ${current.rating}` : null,
  ].filter(Boolean);

  return (
    <section
      className={cn('relative w-full overflow-hidden bg-background', heroHeight)}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {featuredContent.map((item, index) => (
        <div
          key={item.id}
          className={cn(
            'absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700 ease-out',
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          )}
          style={{ backgroundImage: `url(${item.backgroundImage})` }}
        />
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-background/75 via-background/25 to-transparent" />

      <div className="relative z-10 flex h-full items-end">
        <div className="w-full px-4 pb-10 md:px-8 lg:px-10 md:pb-14">
          <div
            key={current.id}
            className="hero-copy-in max-w-xl space-y-3 md:max-w-2xl md:space-y-4"
          >
            {current.isNew && (
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                New
              </span>
            )}

            <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[3.25rem]">
              {current.title}
            </h1>

            {metaParts.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {metaParts.join(' · ')}
              </p>
            )}

            {current.description && (
              <p className="line-clamp-2 max-w-lg text-sm leading-relaxed text-muted-foreground md:line-clamp-3 md:text-base">
                {current.description}
              </p>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                size="lg"
                className="h-11 rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate(`/video/${current.id}`)}
              >
                <Play className="mr-2 h-4 w-4 fill-current" />
                Play
              </Button>
              <Button
                size="lg"
                variant="secondary"
                className="h-11 rounded-md bg-white/10 px-6 text-sm font-semibold text-foreground backdrop-blur-sm hover:bg-white/15"
                onClick={() =>
                  current.slug && navigate(`/watch/${current.slug}`)
                }
              >
                <Info className="mr-2 h-4 w-4" />
                More Info
              </Button>
            </div>
          </div>
        </div>
      </div>

      {total > 1 && (
        <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2 md:bottom-6">
          {featuredContent.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setCurrentSlide(index);
                setIsAutoPlaying(false);
              }}
              aria-label={`Slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : undefined}
              className={cn(
                'h-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                index === currentSlide
                  ? 'w-6 bg-primary'
                  : 'w-1.5 bg-white/30 hover:bg-white/50'
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;
